import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useGate, useUnit } from 'effector-react';
import { Box, Button, FormLabel } from '@mui/material';
import i18n from '@shared/config/i18n';
import { Control, type TreeItem } from '@unicorn/control';
import type { FormInstance } from '@unicorn/effector-form';
import { addFullySelectedParentIds, getValueId, getTicketTypesTree } from './libs';
import { TicketTypesTreeControlGate, ticketTypesTreeQuery } from './model';
import { paidIcon } from './paid-icon';

type Props<T> = {
  form: FormInstance<T>;
  name: string;
  label?: string;
  withAcrchived?: boolean;
  showAcrchived?: boolean;
  isCollapsible?: boolean;
  isSearchable?: boolean;
  visibleItemsLimit?: number;
  showOnlySelectedBranches?: boolean;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  helperText?: string;
};

const archiveIcon =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">' +
      '<path d="m20.54 5.23-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55' +
      'L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5' +
      'c0-.48-.17-.93-.46-1.27M6.24 5h11.52l.81.97H5.44zM5 19V8h14v11zm8.45-9h-2.9v3H8l4 4' +
      ' 4-4h-2.55z"/>' +
      '</svg>'
  );

const ticketTypeIconStyles = {
  '& [data-ticket-type-icon] > .MuiTreeItem-content .MuiTreeItem-label': {
    display: 'block',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  '& [data-ticket-type-icon] > .MuiTreeItem-content .MuiTreeItem-label::after': {
    content: '""',
    display: 'inline-block',
    width: 20,
    height: 20,
    marginLeft: '4px',
    verticalAlign: 'middle',
  },
  '& [data-ticket-type-icon="archived"] > .MuiTreeItem-content .MuiTreeItem-label::after':
    {
      backgroundColor: 'currentColor',
      maskImage: 'url("' + archiveIcon + '")',
      maskPosition: 'center',
      maskRepeat: 'no-repeat',
      maskSize: 'contain',
    },
  '& [data-ticket-type-icon="paid"] > .MuiTreeItem-content .MuiTreeItem-label::after': {
    backgroundImage: 'url("' + paidIcon + '")',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    filter: 'brightness(0) opacity(0.54)',
  },
};

export const TicketTypesTreeControl = <T,>({
  form,
  name,
  withAcrchived,
  showAcrchived = false,
  label,
  required,
  readOnly,
  disabled,
  ...props
}: Props<T>) => {
  useGate(TicketTypesTreeControlGate, { withAcrchived });
  const { data, pending } = useUnit(ticketTypesTreeQuery);
  const [isArchivedShown, setIsArchivedShown] = useState(showAcrchived);

  const ticketTypes = useMemo(
    () =>
      (Array.isArray(data?.types) ? data.types : []).filter(
        ({ archived }) => isArchivedShown || !archived
      ),
    [data, isArchivedShown]
  );

  useEffect(() => {
    setIsArchivedShown(showAcrchived);
  }, [showAcrchived]);

  const handleArchivedVisibilityChange = () => {
    const nextIsArchivedShown = !isArchivedShown;
    setIsArchivedShown(nextIsArchivedShown);

    if (nextIsArchivedShown) {
      return;
    }

    const currentValue = name.split('.').reduce<unknown>((value, key) => {
      if (!value || typeof value !== 'object') {
        return undefined;
      }

      return (value as Record<string, unknown>)[key];
    }, form.$values.getState());

    if (!Array.isArray(currentValue)) {
      return;
    }

    const notArchivedIds = new Set(
      (Array.isArray(data?.types) ? data.types : [])
        .filter(({ archived }) => !archived)
        .map(({ id }) => String(id))
    );
    const nextValue = currentValue.map(String).filter((id) => notArchivedIds.has(id));

    if (nextValue.length !== currentValue.length) {
      form.setValue({ path: name, value: nextValue });
    }
  };

  const items = useMemo(() => getTicketTypesTree(ticketTypes), [ticketTypes]);

  const handleParentClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;

    if (target.closest('.MuiTreeItem-iconContainer')) {
      return;
    }

    const treeItemElement = target.closest<HTMLElement>('[role="treeitem"]');
    if (!treeItemElement) {
      return;
    }

    const findItem = (treeItems: TreeItem[]): TreeItem | undefined => {
      for (const item of treeItems) {
        if (treeItemElement.id.endsWith('-' + String(item.id))) {
          return item;
        }

        const childItem = findItem(item.children ?? []);
        if (childItem) return childItem;
      }

      return undefined;
    };

    const clickedItem = findItem(items);
    if (!clickedItem?.children?.length) {
      return;
    }

    const currentValue = name.split('.').reduce<unknown>((value, key) => {
      if (!value || typeof value !== 'object') {
        return undefined;
      }

      return (value as Record<string, unknown>)[key];
    }, form.$values.getState());

    if (!Array.isArray(currentValue)) {
      return;
    }

    const getItemIds = (item: TreeItem): string[] => [
      getValueId(item.id),
      ...(item.children ?? []).flatMap(getItemIds),
    ];

    const removedIds = new Set(getItemIds(clickedItem));
    const currentIds = currentValue.map(String);

    if (!currentIds.includes(getValueId(clickedItem.id))) {
      return;
    }

    const nextValue = currentIds.filter((id) => !removedIds.has(id));

    event.preventDefault();
    event.stopPropagation();
    form.setValue({ path: name, value: nextValue });
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const subscription = form.setValue.watch(({ path, value }) => {
      if (path !== name || !Array.isArray(value) || !items.length) {
        return;
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        const currentIds = value.map(String);
        const normalizedIds = addFullySelectedParentIds(items, currentIds);
        const currentIdsSet = new Set(currentIds);

        const isNormalized =
          currentIds.length === normalizedIds.length &&
          normalizedIds.every((id) => currentIdsSet.has(id));

        if (!isNormalized) {
          form.setValue({ path, value: normalizedIds });
        }
      }, 0);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [form, items, name]);

  const treeRootRef = useRef<HTMLDivElement>(null);

  const ticketTypeIconsById = useMemo(
    () =>
      new Map(
        ticketTypes
          .filter(({ paid, archived }) => paid || archived)
          .map(({ id, archived }) => [String(id), archived ? 'archived' : 'paid'])
      ),
    [ticketTypes]
  );

  useEffect(() => {
    const root = treeRootRef.current;

    if (!root) {
      return;
    }

    const getTreeItems = (node: ParentNode) => {
      const nestedTreeItems = Array.from(
        node.querySelectorAll<HTMLElement>('[role="treeitem"]')
      );

      return node instanceof HTMLElement && node.matches('[role="treeitem"]')
        ? [node, ...nestedTreeItems]
        : nestedTreeItems;
    };

    const decorateTreeItem = (treeItem: HTMLElement) => {
      const ticketTypeId = treeItem.id.match(/(\d+)$/)?.[1];
      const icon = ticketTypeId ? ticketTypeIconsById.get(ticketTypeId) : undefined;

      if (icon) {
        treeItem.dataset.ticketTypeIcon = icon;
      } else {
        delete treeItem.dataset.ticketTypeIcon;
      }
    };

    getTreeItems(root).forEach(decorateTreeItem);

    const pendingTreeItems = new Set<HTMLElement>();
    let decorationFrameId: number | null = null;
    const decoratePendingTreeItems = () => {
      Array.from(pendingTreeItems)
        .slice(0, 100)
        .forEach((treeItem) => {
          pendingTreeItems.delete(treeItem);

          if (root.contains(treeItem)) {
            decorateTreeItem(treeItem);
          }
        });

      decorationFrameId = pendingTreeItems.size
        ? requestAnimationFrame(decoratePendingTreeItems)
        : null;
    };

    const scheduleDecoration = (node: ParentNode) => {
      getTreeItems(node).forEach((treeItem) => pendingTreeItems.add(treeItem));

      if (decorationFrameId === null) {
        decorationFrameId = requestAnimationFrame(decoratePendingTreeItems);
      }
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            scheduleDecoration(node);
          }
        });
      });
    });

    observer.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();

      if (decorationFrameId !== null) {
        cancelAnimationFrame(decorationFrameId);
      }
    };
  }, [ticketTypeIconsById]);

  return (
    <Box
      ref={treeRootRef}
      sx={ticketTypeIconStyles}
      onClickCapture={handleParentClickCapture}
    >
      {withAcrchived && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '4px',
          }}
        >
          {label && (
            <FormLabel required={required} disabled={disabled}>
              {label}
            </FormLabel>
          )}
          <Button
            variant="text"
            size="small"
            disabled={disabled}
            onClick={handleArchivedVisibilityChange}
            sx={{ marginLeft: 'auto' }}
          >
            {i18n.t(isArchivedShown ? 'HideArchived' : 'ShowArchived')}
          </Button>
        </Box>
      )}
      <Control.Tree
        form={form}
        name={name}
        label={withAcrchived ? undefined : label}
        items={items}
        isLoading={pending}
        required={required}
        readOnly={readOnly}
        disabled={disabled}
        {...props}
      />
    </Box>
  );
};
